import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGraduationCap, faChartLine, faUpload, faSearch,
  faClipboardCheck, faCog, faSignOutAlt, faChartBar
} from "@fortawesome/free-solid-svg-icons";
import { hasCapability } from "../config/roles";
import "./Header.css";
import bitsLogo from "../assets/bits_logo.png";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [error, setError] = useState(null);

  const [userData, setUserData] = useState({});
  const roles = Array.isArray(userData?.roles) ? userData.roles : [];

  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      const response = await axios.get(`/qpra/logout`, { withCredentials: true });
      if (response.data?.success && response.data?.redirect_url) {
        window.location.href = response.data.redirect_url;
      } else {
        console.warn('Logout API returned unexpected response:', response.data);
      }
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/qpra/get-saml-data`, {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        const data = await res.json();
        setUserData(data || {});
        setError(null);
      } catch (err) {
        console.error("Error fetching SAML data:", err);
        setUserData({});
        setError(err.message || "Unknown error");
      } finally {
        setAuthLoading(false);
      }
    })();
  }, []);

  // ---- Role helpers ----
  // Capabilities come from config/roles.js, the same table App.jsx builds its
  // route guards from, so a nav item and its route can no longer disagree about
  // who may reach it.
  const canShow = (capability) => !authLoading && hasCapability(roles, capability);

  return (
    <header className={`header ${isScrolled ? "scrolled" : ""}`}>
      <div className="container-header">
        <div className="header-content">
          {/* Logo */}
          <Link to="/" className="logo">
            <img src={bitsLogo} alt="BITS Logo" className="bits-logo-img" />
            <span className="logo-subtitle">Question Review System</span>
          </Link>

          <nav className="nav-desktop">
            {/* Data Upload */}
            {canShow("csvUpload") && (
              <Link
                to="/data_operator_upload"
                className={`nav-link ${isActive('/data_operator_upload') ? 'active' : ''}`}
              >
                <FontAwesomeIcon icon={faUpload} />
                CSV Upload
              </Link>
            )}

            {/* Reviewer Assigner */}
            {canShow("reviewerAssigner") && (
              <Link
                to="/assigner_page"
                className={`nav-link ${isActive('/assigner_page') ? 'active' : ''}`}
              >
                <FontAwesomeIcon icon={faChartLine} />
                Reviewer Assigner
              </Link>
            )}

            {/* Review */}
            {canShow("review") && (
              <Link
                to="/review_page"
                className={`nav-link ${isActive('/review_page') ? 'active' : ''}`}
              >
                <FontAwesomeIcon icon={faClipboardCheck} />
                Review Papers
              </Link>
            )}

            {/* Upload Files */}
            {canShow("uploadPapers") && (
              <Link
                to="/upload_page"
                className={`nav-link ${isActive('/upload_page') ? 'active' : ''}`}
              >
                <FontAwesomeIcon icon={faUpload} />
                Upload Files
              </Link>
            )}

            {/* Rubric Management */}
            {canShow("rubricManagement") && (
              <Link
                to="/rubric_management"
                className={`nav-link ${isActive('/rubric_management') ? 'active' : ''}`}
              >
                <FontAwesomeIcon icon={faCog} />
                Rubric Management
              </Link>
            )}

            {/* Analytics */}
            {canShow("analytics") && (
              <Link
                to="/analytics"
                className={`nav-link ${isActive('/analytics') ? 'active' : ''}`}
              >
                <FontAwesomeIcon icon={faChartBar} />
                Analytics
              </Link>
            )}
          </nav>
        </div>

        {/* Logout pinned to the far right */}
        <div className="header-actions">
          <button
            className="logout-button"
            onClick={handleLogout}
            title="Logout"
            aria-label="Logout"
            disabled={isLoggingOut}
          >
            <FontAwesomeIcon icon={faSignOutAlt} className="logout-icon" />
            <span className="logout-text">Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
